import {
  isComplianceSubdomain,
  isDutyChargesAllowedSubdomain,
  isPurposeOfShipmentRequired,
} from '../../../utils/getSubdomain';
import { logSaveSkipReason } from '../../../utils/logSaveSkipReason';
import React, { useContext, useEffect, useMemo } from 'react';
import Dropdown from '@Components/Dropdown';
import SbTextField from '@Components/Textfield';
import SbButton from '@Components/Button';
import {
  Checkbox,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  Divider,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { isExportShipment } from '../../../utils/exportShipment';
import { useSnackbar } from 'notistack';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { SHIPMENT_MODES, SHIPMENT_UNITS, INCOTERM_OPTIONS, defaultBoxState } from '../../CreateShipment/constants';
import { SHIPMENT_TYPE } from '@Constants/Shipment';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { setInsureShipment, updateShipment } from '@Reducers/AdminShipment';
import { getSingleShipmentPackagePayload, getBoxStateObj, getUserDetailsStateObj } from '@Services/getShipmentPayload';
import { ShipmentControl } from '@Services/shipmentActionControl';
import CreateShipmentContext from '../../../context/CreateShipment';
import CreateShipmentWrapper from '../../../context/CreateShipment/Provider';
import { LengthType, createShipmentContextType } from '../../../types/shipment';
import { isActionAllowed } from '../../../utils/allowedActions';
import { hasAdminAccess } from '../../../services/hasAdminAccess';
import { isEUCountry, isComplianceRequired, isVatPayloadApplicable } from '../../../utils/euCompliance';
import { isUSDestination, validateCpscCompliance, applyCpscAutoSetForC2CSampleGift } from '../../../utils/usCompliance';
import ShipmentDetailsBoxWise from '../../CreateShipment/ShipmentDetails';
import useScroll from '../useScroll';
import Actions from './Actions';

const getCountryName = (field: any): string => {
  if (!field) return '';
  if (typeof field === 'string') return field.trim();
  if (typeof field === 'object') {
    if (typeof field.country === 'string') return field.country.trim();
    if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
      return field.country.name.trim();
    }
    if (typeof field.name === 'string') return field.name.trim();
  }
  return '';
};

const SHIPMENT_TYPE_LITS = [
  { value: 'parcel', display: 'Parcel' },
  { value: 'document', display: 'Document' },
];

const getDutyChargeType = (dutyCharge: any): string => {
  if (!dutyCharge) return '';
  if (typeof dutyCharge === 'string') return dutyCharge;
  if (typeof dutyCharge === 'object' && typeof dutyCharge.type === 'string') return dutyCharge.type;
  return '';
};

type StatusDetail = {
  status: string;
  [key: string]: any;
};

type Shipment = {
  statusDetails: StatusDetail[];
  [key: string]: any;
};

function isInTransit(shipment?: Shipment): boolean {
  if (!shipment || !shipment.statusDetails) return false;
  return shipment.statusDetails.some(detail => detail.status === 'in_transit');
}

function ShipmentDetails() {
  const dispatch = useDispatch<AppDispatch>();
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const { name = '' } = organizationData || {};
  const { mutlipleShipmentsData } = useSelector((state: RootStateType) => state.trackShipment) || [];
  const checkForIsInTransit = isInTransit(mutlipleShipmentsData?.[0]);

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isShipmentDetailsWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_details,
    PERFORM_ACTION.write,
  );

  const isInTransitEditAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.edit_package_after_in_transit,
    PERFORM_ACTION.write,
  );

  const { shipmentDetailsRef } = useScroll();
  const {
    consignee,
    boxes,
    setBoxes,
    setRemoveFirstBoxDeleteOption,
    packageData,
    shipmentType,
    pickupType,
    setShipmentType,
    setPackageData,
    boxValidation,
    // selectedBox,
    setSelectedBox,
    setSelectedProduct,
    extraDetails,
    setExtraDetails,
    setConsignee,
  } = useContext(CreateShipmentContext) as createShipmentContextType;
  const { enqueueSnackbar } = useSnackbar();
  const [vatTouched, setVatTouched] = React.useState(false);

  const handleShipmentModeChange = (newMode: string) => {
    const isNewB2B = newMode === 'B2B';
    const updatedShipment = { ...shipmentType, shipmentMode: newMode };
    const updatedExtra = { ...extraDetails };
    if (!isNewB2B || !isDestinationEU) {
      updatedShipment.recipientVatTaxId = '';
      updatedExtra.recipientVatTaxId = '';
    }
    setVatTouched(false);

    const isNewC2C = (newMode || '').trim().toLowerCase() === 'c2c';
    const cleanPurpose = (extraDetails?.purposeOfShipment || '').trim().toLowerCase();
    if (
      isUSDestination(destCountry) &&
      isNewC2C &&
      (cleanPurpose === 'sample' || cleanPurpose === 'gift') &&
      boxes?.length
    ) {
      const { updatedBoxes, didUpdate } = applyCpscAutoSetForC2CSampleGift(
        boxes,
        newMode,
        extraDetails?.purposeOfShipment,
        destCountry,
      );
      if (didUpdate) {
        setBoxes(updatedBoxes);
      }
    }

    setShipmentType(updatedShipment);
    setPackageData({
      ...packageData,
      recipientVatTaxId: updatedShipment.recipientVatTaxId,
      iossNumber: updatedShipment.iossNumber,
    });
    setExtraDetails(updatedExtra);
  };

  const handleIncotermChange = (newIncoterm: string) => {
    const updatedShipment = { ...shipmentType, incoterm: newIncoterm };
    const updatedExtra = { ...extraDetails, incoterm: newIncoterm };

    if (newIncoterm === 'FOB') {
      updatedShipment.freight = 0;
      updatedShipment.freightInsurance = 0;
      updatedExtra.freight = 0;
      updatedExtra.freightInsurance = 0;
    } else if (newIncoterm === 'CNF') {
      updatedShipment.freightInsurance = 0;
      updatedExtra.freightInsurance = 0;
    }

    setShipmentType(updatedShipment);
    setPackageData({
      ...packageData,
      incoterm: newIncoterm,
      freight: updatedShipment.freight,
      freightInsurance: updatedShipment.freightInsurance,
    });
    setExtraDetails(updatedExtra);
  };

  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const shipmentActionControl = new ShipmentControl(shipmentById);

  useEffect(() => {
    if (shipmentById) {
      const { _packageId, shipmentMode, extraDetails, userReferenceNumber } = shipmentById;
      const { box, insurance, packaging, isDocument, unitOfMeasurement, currency, invoiceNumber } = _packageId || {};
      const rawInvoice = invoiceNumber ?? (shipmentById as any)?.invoiceNumber ?? '';

      const resolvedMode = shipmentMode || '';
      const resolvedUnit = unitOfMeasurement === 'lb-in' ? 'lb-in' : 'kg-cm';
      const resolvedInvoice = rawInvoice !== undefined && rawInvoice !== null ? String(rawInvoice) : '';

      console.info('[ShipmentDetails Hydration] start', {
        shipmentMode: resolvedMode,
        unitOfMeasurement: resolvedUnit,
        invoiceNumber: resolvedInvoice,
      }); // TODO: remove after verification

      const boxData = getBoxStateObj(box || []);
      setBoxes(boxData);
      setRemoveFirstBoxDeleteOption(true);

      const anyData = shipmentById as any;
      const incoterm =
        anyData?.incoterm || (_packageId as any)?.incoterm || (extraDetails?.other as any)?.incoterm || 'FOB';
      const freight = anyData?.freight ?? (_packageId as any)?.freight ?? (extraDetails?.other as any)?.freight ?? 0;
      const freightInsurance =
        anyData?.freightInsurance ??
        (_packageId as any)?.freightInsurance ??
        (extraDetails?.other as any)?.freightInsurance ??
        0;
      const iossNumber =
        anyData?.iossNumber || (_packageId as any)?.iossNumber || (extraDetails?.other as any)?.iossNumber || '';
      const recipientVatTaxId =
        (shipmentById?._consigneeId as any)?.vat ||
        (shipmentById as any)?.consignee?.vat ||
        anyData?.consignee?.vat ||
        anyData?._consigneeId?.vat ||
        anyData?.recipientVatTaxId ||
        (_packageId as any)?.recipientVatTaxId ||
        (extraDetails?.other as any)?.recipientVatTaxId ||
        '';
      const iorEoriNumber =
        anyData?.iorEoriNumber ||
        (_packageId as any)?.iorEoriNumber ||
        extraDetails?.other?.ior ||
        (extraDetails?.other as any)?.iorEoriNumber ||
        '';

      setShipmentType({
        ...shipmentType,
        type: isDocument ? 'document' : 'parcel',
        shipmentMode: resolvedMode || shipmentType.shipmentMode,
        currency: currency || shipmentType.currency,
        unit: resolvedUnit,
        invoiceNumber: resolvedInvoice || shipmentType.invoiceNumber,
        referenceNumber: userReferenceNumber || shipmentType.referenceNumber,
        dutyCharge: (() => {
          const rawDuty =
            (shipmentById as any)?.dutyCharge ||
            (shipmentById as any)?._packageId?.dutyCharge ||
            (shipmentById as any)?.package?.dutyCharge ||
            shipmentType.dutyCharge;
          const rawType = getDutyChargeType(rawDuty);
          const rawCharge =
            typeof rawDuty === 'object' && rawDuty?.charge !== undefined
              ? rawDuty.charge
              : (shipmentById as any)?.hurricaneDutyAndTax ?? 0;
          return rawType ? { type: rawType, charge: rawCharge } : '';
        })(),
        incoterm,
        freight,
        freightInsurance,
        iossNumber,
        recipientVatTaxId,
        iorEoriNumber,
      });
      setPackageData({
        ...packageData,
        unitOfMeasurement: resolvedUnit,
        insurance: insurance ?? packageData.insurance,
        packaging: packaging ?? packageData.packaging,
        userReferenceNumber: userReferenceNumber || packageData.userReferenceNumber,
        incoterm,
        freight,
        freightInsurance,
        iossNumber,
        recipientVatTaxId,
        iorEoriNumber,
      });
      if (boxData.length) {
        setSelectedBox(boxData[0]);
      } else {
        setSelectedBox({ ...defaultBoxState });
      }
      setSelectedProduct(null);
      if (shipmentById?._consigneeId && setConsignee) {
        setConsignee(getUserDetailsStateObj(shipmentById._consigneeId));
      }
      const rawPurpose =
        extraDetails?.other?.purposeOfShipment ||
        (extraDetails as any)?.purposeOfShipment ||
        (_packageId as any)?.purposeOfShipment ||
        anyData?.purposeOfShipment ||
        '';

      setExtraDetails({
        ...(extraDetails?.other || {}),
        purposeOfShipment: rawPurpose,
        incoterm,
        freight,
        freightInsurance,
        iossNumber,
        recipientVatTaxId,
        iorEoriNumber,
        ior: iorEoriNumber || extraDetails?.other?.ior || '',
      });

      console.info('[ShipmentDetails Hydration] end', {
        shipmentMode: resolvedMode,
        unitOfMeasurement: resolvedUnit,
        invoiceNumber: resolvedInvoice,
      }); // TODO: remove after verification
    }
  }, [shipmentById]);

  const { currencyList } = useSelector((state: RootStateType) => state?.global);

  const destCountry = getCountryName(shipmentById?._consigneeId);
  const isDestinationEU = useMemo(() => isEUCountry(destCountry), [destCountry]);
  const isDocument = (shipmentType.type || '').toLowerCase() === 'document';
  const orgCountryName = organizationData?._country?.name?.trim() || 'India';
  const isExport = isExportShipment({
    orgCountryName,
    shipper: shipmentById?._shipperId,
    consignee: shipmentById?._consigneeId || consignee,
    shipmentById,
  });
  const isComplianceActive = isComplianceSubdomain();
  const isB2B = (shipmentType.shipmentMode || '').toUpperCase() === 'B2B';
  const isVatVisible = isComplianceActive && !isDocument && isB2B && isDestinationEU;
  const isVatRequired = isVatVisible;
  const isIossVisible = isDestinationEU;
  const currentIncoterm = shipmentType.incoterm || 'FOB';
  const isCIF = currentIncoterm === 'CIF';
  const isCNF = currentIncoterm === 'CNF';
  const hasFreightInsuranceSection = isCIF || isCNF;
  const numericFreight = Number(shipmentType.freight || 0);
  const numericInsurance = Number(shipmentType.freightInsurance || 0);

  const resolvedShipmentValue = useMemo(() => {
    let sum = 0;
    (boxes || []).forEach(b => {
      (b.products || []).forEach((p: any) => {
        const q = Number(p.quantity ?? p.qty ?? 0);
        const v = Number(p.unitValue ?? p.value ?? 0);
        if (q > 0 && v > 0) sum += q * v;
      });
    });
    return Number(sum.toFixed(2));
  }, [boxes]);

  const totalCombinedValue = resolvedShipmentValue + numericFreight + numericInsurance;

  const rawAdminDutyTax =
    (shipmentById as any)?.hurricaneDutyAndTax ??
    (shipmentById as any)?._packageId?.hurricaneDutyAndTax ??
    (shipmentById as any)?.package?.hurricaneDutyAndTax ??
    (typeof (shipmentById as any)?.dutyCharge === 'object' ? (shipmentById as any)?.dutyCharge?.charge : null) ??
    (typeof (shipmentById as any)?._packageId?.dutyCharge === 'object'
      ? (shipmentById as any)?._packageId?.dutyCharge?.charge
      : null) ??
    (typeof shipmentType.dutyCharge === 'object' ? shipmentType.dutyCharge?.charge : null);

  const hasDutyTaxValue =
    rawAdminDutyTax !== null &&
    rawAdminDutyTax !== undefined &&
    rawAdminDutyTax !== '' &&
    !isNaN(Number(rawAdminDutyTax));

  const dutyTaxFormatted = hasDutyTaxValue
    ? `${shipmentType.currency || 'INR'} ${Number(rawAdminDutyTax).toFixed(2)}`
    : '—';

  const handleDutyChargeChange = (mode: 'DDU' | 'DDP') => {
    const chargeAmount = typeof shipmentType.dutyCharge === 'object' ? shipmentType.dutyCharge?.charge || 0 : 0;
    const updated = {
      ...shipmentType,
      dutyCharge: {
        type: mode,
        charge: chargeAmount,
      },
    };
    setShipmentType(updated);
    setPackageData({
      ...packageData,
      dutyCharge: updated.dutyCharge,
    });
  };

  const isSaveDisabled = useMemo(() => {
    if (!isExport || isDocument) return false;

    const incoterm = (shipmentType.incoterm || '').trim().toUpperCase();
    if (!incoterm) return true;

    if (incoterm === 'CIF') {
      const fr = Number(shipmentType.freight);
      const ins = Number(shipmentType.freightInsurance);
      if (!fr || fr <= 0 || !ins || ins <= 0) return true;
    } else if (incoterm === 'CNF') {
      const fr = Number(shipmentType.freight);
      if (!fr || fr <= 0) return true;
    }

    const dutyType = getDutyChargeType(shipmentType.dutyCharge);
    if (!dutyType || (dutyType !== 'DDP' && dutyType !== 'DDU')) return true;

    return false;
  }, [
    isExport,
    isDocument,
    shipmentType.incoterm,
    shipmentType.freight,
    shipmentType.freightInsurance,
    shipmentType.dutyCharge,
  ]);

  const onSave = () => {
    const isPurposeRequired = isPurposeOfShipmentRequired(shipmentType);
    const purposeVal = (extraDetails?.purposeOfShipment || '').trim();
    if (isPurposeRequired && !purposeVal) {
      logSaveSkipReason('Box Dimensions', 'validation failed', { invalidField: 'Purpose Of Shipment', purposeVal }); // TODO: remove after verification
      enqueueSnackbar('Purpose Of Shipment is required', { variant: 'warning' });
      return;
    }

    if (isExport && !isDocument) {
      const incoterm = (shipmentType.incoterm || '').trim().toUpperCase();
      if (!incoterm) {
        logSaveSkipReason('Box Dimensions', 'validation failed', { invalidField: 'Incoterm' }); // TODO: remove after verification
        enqueueSnackbar('Incoterm is required', { variant: 'warning' });
        return;
      }
      if (incoterm === 'CIF') {
        const fr = Number(shipmentType.freight);
        const ins = Number(shipmentType.freightInsurance);
        if (!fr || fr <= 0) {
          logSaveSkipReason('Box Dimensions', 'validation failed', { invalidField: 'Freight' }); // TODO: remove after verification
          enqueueSnackbar('Freight amount must be greater than 0 for CIF', { variant: 'warning' });
          return;
        }
        if (!ins || ins <= 0) {
          logSaveSkipReason('Box Dimensions', 'validation failed', { invalidField: 'Insurance' }); // TODO: remove after verification
          enqueueSnackbar('Insurance amount must be greater than 0 for CIF', { variant: 'warning' });
          return;
        }
      } else if (incoterm === 'CNF') {
        const fr = Number(shipmentType.freight);
        if (!fr || fr <= 0) {
          logSaveSkipReason('Box Dimensions', 'validation failed', { invalidField: 'Freight' }); // TODO: remove after verification
          enqueueSnackbar('Freight amount must be greater than 0 for CNF', { variant: 'warning' });
          return;
        }
      }

      const dutyType = getDutyChargeType(shipmentType.dutyCharge);
      if (!dutyType || (dutyType !== 'DDP' && dutyType !== 'DDU')) {
        logSaveSkipReason('Box Dimensions', 'validation failed', { invalidField: 'dutyCharge' }); // TODO: remove after verification
        enqueueSnackbar('Please select who pays duty & taxes (DDP / DDU)', { variant: 'warning' });
        return;
      }
    }

    const vatVal = (shipmentType?.recipientVatTaxId || extraDetails?.recipientVatTaxId || '').trim();
    if (isVatRequired && !vatVal) {
      logSaveSkipReason('Box Dimensions', 'validation failed', { invalidField: 'Recipient VAT / Tax ID', vatVal }); // TODO: remove after verification
      setVatTouched(true);
      enqueueSnackbar('Recipient VAT / Tax ID is required for B2B shipments to EU', { variant: 'warning' });
      return;
    }

    const isUS = isUSDestination(destCountry);
    const complianceRequired = isComplianceRequired();
    if (isUS && complianceRequired) {
      let missingCpscMsg = '';
      for (let bIdx = 0; bIdx < (boxes || []).length; bIdx++) {
        const box = boxes[bIdx];
        for (let pIdx = 0; pIdx < (box.products || []).length; pIdx++) {
          const prod = box.products[pIdx];
          const cpscVal = validateCpscCompliance(prod.cpscCompliance, true, true, hasAdminAccess());
          if (!cpscVal.isValid) {
            missingCpscMsg = `Box ${bIdx + 1}, Item ${pIdx + 1}: ${Object.values(cpscVal.errors)[0]}`;
            break;
          }
        }
        if (missingCpscMsg) break;
      }
      if (missingCpscMsg) {
        logSaveSkipReason('Box Dimensions', 'validation failed', {
          invalidField: 'US CPSC compliance',
          error: missingCpscMsg,
        }); // TODO: remove after verification
        enqueueSnackbar(missingCpscMsg, { variant: 'warning' });
        return;
      }
    }

    const packageObj = getSingleShipmentPackagePayload({
      boxes,
      packageData: {
        ...packageData,
        incoterm: shipmentType.incoterm,
        freight: shipmentType.freight,
        freightInsurance: shipmentType.freightInsurance,
        iossNumber: shipmentType.iossNumber,
        iorEoriNumber: shipmentType.iorEoriNumber,
        ...(name === 'Cargo Solutions Network' && { isDropOff: pickupType.type === 'dropOff' }),
      },
      shipmentType: { ...shipmentType, unit: packageData.unitOfMeasurement as LengthType },
      pickupType,
      destinationCountry:
        consignee?.country || (shipmentById as any)?._consigneeId?.country || (shipmentById as any)?.consignee?.country,
      extraDetails,
    });

    const isAllowedDutySubdomain =
      isDutyChargesAllowedSubdomain() || Boolean(getDutyChargeType(shipmentType.dutyCharge));
    const currentType = getDutyChargeType(shipmentType.dutyCharge);
    const currentCharge = typeof shipmentType.dutyCharge === 'object' ? shipmentType.dutyCharge.charge || 0 : 0;
    const formattedDutyCharge =
      isAllowedDutySubdomain && currentType
        ? {
            type: currentType,
            charge: currentCharge,
          }
        : undefined;

    if (packageObj && isAllowedDutySubdomain && formattedDutyCharge) {
      packageObj.dutyCharge = formattedDutyCharge;
    }

    let idToUse;
    let isAccountHolder;
    if (shipmentById) {
      idToUse = shipmentById._accountId?.isAccountHolder ? shipmentById._accountId?._id : shipmentById?._userId?._id;
      isAccountHolder = shipmentById._accountId?.isAccountHolder;
    }

    delete (packageObj as any).iossNumber;
    const rawIoss = (shipmentType.iossNumber || extraDetails?.iossNumber || '').trim();

    if (!shipmentById?.shipmentNumber) {
      logSaveSkipReason('Box Dimensions', 'form not ready / missing shipmentId', {
        shipmentNumber: shipmentById?.shipmentNumber,
      }); // TODO: remove after verification
      return;
    }

    if (!boxValidation) {
      logSaveSkipReason('Box Dimensions', 'validation failed', { invalidField: 'boxValidation', boxValidation }); // TODO: remove after verification
      return;
    }

    const updatedExtraOther = {
      ...extraDetails,
      incoterm: shipmentType.incoterm || 'FOB',
      freight: Number(shipmentType.freight || 0),
      freightInsurance: Number(shipmentType.freightInsurance || 0),
      iossNumber: shipmentType.iossNumber || '',
      iorEoriNumber: shipmentType.iorEoriNumber || extraDetails?.ior || '',
      ior: shipmentType.iorEoriNumber || extraDetails?.ior || '',
    };

    const existingConsignee = shipmentById?._consigneeId || (shipmentById as any)?.consignee || {};
    const isDoc =
      shipmentType.type === 'document' ||
      shipmentById?._packageId?.isDocument === true ||
      (shipmentById as any)?.type === 'document';
    const isVatApplicable = isVatPayloadApplicable({
      destCountry,
      shipmentMode: shipmentType.shipmentMode || shipmentById?.shipmentMode,
      isDocument: isDoc,
    });
    const consigneePayload: any = {
      ...existingConsignee,
    };
    if (!isVatApplicable) {
      delete consigneePayload.vat;
    } else {
      consigneePayload.vat = isVatVisible ? vatVal : '';
    }

    const updatePayload = {
      shipmentNumber: shipmentById.shipmentNumber,
      userReferenceNumber: shipmentType.referenceNumber,
      shipmentMode: shipmentType.shipmentMode,
      consignee: consigneePayload,
      package: packageObj,
      iossNumber: rawIoss,
      purposeOfShipment: extraDetails?.purposeOfShipment || updatedExtraOther?.purposeOfShipment || 'FOR_SALE',
      extraDetails: {
        other: updatedExtraOther,
        purposeOfShipment: extraDetails?.purposeOfShipment || updatedExtraOther?.purposeOfShipment || 'FOR_SALE',
      },
      accountId: idToUse,
      isAccountHolder: isAccountHolder,
      ...(isAllowedDutySubdomain &&
        formattedDutyCharge && {
          dutyCharge: formattedDutyCharge,
        }),
    };

    console.info('[Box Dimensions Save] calling API', updatePayload); // TODO: remove after verification
    dispatch(updateShipment(updatePayload));
  };

  useEffect(() => {
    dispatch(setInsureShipment(packageData.insurance));
  }, [packageData.insurance]);

  const theme = useTheme();

  return (
    <Box ref={shipmentDetailsRef} sx={{ mb: 3, width: '100%', maxWidth: '100%' }}>
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: '14px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
          mb: 0,
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TuneOutlinedIcon />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                Shipment Specifications
              </Typography>
              <Chip
                label={shipmentType.type === 'document' ? 'Document' : 'Parcel'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
              Define shipment type, measurement units, and invoice declarations
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, width: '100%', boxSizing: 'border-box' }}>
          <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="ShipmentType"
                required
                label="Shipment Type"
                name="name"
                options={SHIPMENT_TYPE_LITS}
                value={shipmentType.type ? [shipmentType.type] : []}
                onChange={([value]) => {
                  const isNewDoc = value === 'document';
                  const updated: any = { ...shipmentType, type: value };
                  if (isNewDoc) {
                    updated.recipientVatTaxId = '';
                    setExtraDetails({ ...extraDetails, recipientVatTaxId: '' });
                    setVatTouched(false);
                  }
                  setShipmentType(updated);
                }}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="ShipmentMode"
                required
                label="Shipment Mode"
                name="shipmentMode"
                options={SHIPMENT_MODES}
                value={shipmentType.shipmentMode ? [shipmentType.shipmentMode] : []}
                onChange={([value]) => handleShipmentModeChange(value)}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="preferredUnit"
                required
                label="Preferred Unit"
                name="name"
                options={SHIPMENT_UNITS}
                value={packageData.unitOfMeasurement ? [packageData.unitOfMeasurement] : []}
                onChange={([value]) => setPackageData({ ...packageData, unitOfMeasurement: value })}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="Currency"
                required
                label="Currency"
                name="name"
                options={currencyList}
                value={shipmentType.currency ? [shipmentType.currency] : []}
                onChange={([value]) => setShipmentType({ ...shipmentType, currency: value })}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SbTextField
                label="No Of Boxes"
                name="name"
                value={shipmentById?._packageId?.box?.length || ''}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SbTextField
                label="Reference No."
                name="name"
                value={shipmentType?.referenceNumber}
                onChange={e => setShipmentType({ ...shipmentType, referenceNumber: e.target.value })}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SbTextField
                label="Invoice No."
                name="name"
                disabled={!isShipmentDetailsWriteAllowed}
                value={shipmentType?.invoiceNumber || ''}
                onChange={e => setShipmentType({ ...shipmentType, invoiceNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Dropdown
                id="purposeOfShipment"
                required={isPurposeOfShipmentRequired(shipmentType)}
                label="Purpose Of Shipment"
                name="name"
                options={SHIPMENT_TYPE}
                value={extraDetails.purposeOfShipment ? [extraDetails.purposeOfShipment] : []}
                onChange={([value]) => {
                  setExtraDetails({ ...extraDetails, purposeOfShipment: value });
                  const isC2C = (shipmentType.shipmentMode || '').trim().toLowerCase() === 'c2c';
                  const cleanPurpose = (value || '').trim().toLowerCase();
                  if (
                    isUSDestination(destCountry) &&
                    isC2C &&
                    (cleanPurpose === 'sample' || cleanPurpose === 'gift') &&
                    boxes?.length
                  ) {
                    const { updatedBoxes, didUpdate } = applyCpscAutoSetForC2CSampleGift(
                      boxes,
                      shipmentType.shipmentMode,
                      value,
                      destCountry,
                    );
                    if (didUpdate) {
                      setBoxes(updatedBoxes);
                    }
                  }
                }}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>

            {isIossVisible && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SbTextField
                  label="IOSS Number"
                  name="iossNumber"
                  placeholder="Enter IOSS Number"
                  value={shipmentType?.iossNumber || extraDetails?.iossNumber || ''}
                  onChange={e => {
                    setShipmentType({ ...shipmentType, iossNumber: e.target.value });
                    setPackageData({ ...packageData, iossNumber: e.target.value });
                    setExtraDetails({ ...extraDetails, iossNumber: e.target.value });
                  }}
                  disabled={!isShipmentDetailsWriteAllowed}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SbTextField
                label="IOR/EORI No."
                name="iorEoriNumber"
                placeholder="Enter IOR / EORI No."
                value={shipmentType?.iorEoriNumber || extraDetails?.ior || ''}
                onChange={value => {
                  setShipmentType({ ...shipmentType, iorEoriNumber: value.target.value });
                  setPackageData({ ...packageData, iorEoriNumber: value.target.value });
                  setExtraDetails({
                    ...extraDetails,
                    ior: value.target.value,
                    iorEoriNumber: value.target.value,
                  });
                }}
                disabled={!isShipmentDetailsWriteAllowed}
              />
            </Grid>
            {isVatVisible && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SbTextField
                  required={isVatRequired}
                  label={`Recipient VAT / Tax ID${isVatRequired ? '' : ''}`}
                  name="recipientVatTaxId"
                  placeholder="Enter Recipient VAT / Tax ID"
                  value={shipmentType?.recipientVatTaxId || extraDetails?.recipientVatTaxId || ''}
                  onChange={e => {
                    setShipmentType({ ...shipmentType, recipientVatTaxId: e.target.value });
                    setPackageData({ ...packageData, recipientVatTaxId: e.target.value });
                    setExtraDetails({ ...extraDetails, recipientVatTaxId: e.target.value });
                  }}
                  onBlur={() => setVatTouched(true)}
                  error={
                    isVatRequired &&
                    vatTouched &&
                    !(shipmentType?.recipientVatTaxId || extraDetails?.recipientVatTaxId)?.trim()
                      ? 'Recipient VAT / Tax ID is required for B2B shipments to EU'
                      : ''
                  }
                  helperText="Required for B2B commercial shipments to EU"
                  disabled={!isShipmentDetailsWriteAllowed}
                />
              </Grid>
            )}
          </Grid>

          {/* Incoterms & Commercial Terms Section */}
          {isExport && !isDocument && (
            <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PaymentsOutlinedIcon />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                      Incoterms & Commercial Terms
                    </Typography>
                    <Chip
                      label={currentIncoterm}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                    Specify the international commercial terms and declared freight & insurance charges
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: hasFreightInsuranceSection ? 2.5 : 0 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1 }}>
                  Select Incoterm *
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 0.5,
                    bgcolor: alpha(theme.palette.divider, 0.08),
                    borderRadius: '10px',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {INCOTERM_OPTIONS.map(opt => {
                    const isSelected = currentIncoterm === opt.value;
                    const formattedLabel = opt.display.replace(' - ', ' — ');
                    return (
                      <SbButton
                        key={opt.value}
                        variant={isSelected ? 'contained' : 'text'}
                        disabled={!isShipmentDetailsWriteAllowed}
                        onClick={() => handleIncotermChange(opt.value)}
                        sx={{
                          px: 2.5,
                          py: 1,
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          bgcolor: isSelected ? 'primary.main' : 'transparent',
                          color: isSelected ? 'common.white' : 'text.primary',
                          boxShadow: isSelected ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                          '&:hover': {
                            bgcolor: isSelected ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                          },
                        }}
                      >
                        {formattedLabel}
                      </SbButton>
                    );
                  })}
                </Box>
              </Box>

              {/* Freight & Insurance Subsection (Conditional on CIF / CNF) */}
              {hasFreightInsuranceSection && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: '12px',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PaymentsOutlinedIcon color="primary" fontSize="small" />
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                      Freight & Insurance ({currentIncoterm})
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '14px', display: 'block', mb: 2 }}
                  >
                    {isCIF
                      ? 'CIF Incoterm includes both Freight and Insurance charges in the declaration.'
                      : 'CNF Incoterm includes Freight charges; Insurance is optional.'}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <SbTextField
                        required
                        type="number"
                        label={`Freight (${shipmentType.currency || 'INR'})`}
                        placeholder="Enter freight amount"
                        name="freight"
                        value={shipmentType.freight !== undefined ? shipmentType.freight : ''}
                        onChange={e => {
                          setShipmentType({ ...shipmentType, freight: e.target.value });
                          setPackageData({ ...packageData, freight: e.target.value });
                          setExtraDetails({ ...extraDetails, freight: e.target.value });
                        }}
                        disabled={!isShipmentDetailsWriteAllowed}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <SbTextField
                        required={isCIF}
                        type="number"
                        label={`Insurance (${shipmentType.currency || 'INR'})${isCIF ? '' : ' (Optional)'}`}
                        placeholder="Enter insurance amount"
                        name="freightInsurance"
                        value={shipmentType.freightInsurance !== undefined ? shipmentType.freightInsurance : ''}
                        onChange={e => {
                          setShipmentType({ ...shipmentType, freightInsurance: e.target.value });
                          setPackageData({ ...packageData, freightInsurance: e.target.value });
                          setExtraDetails({ ...extraDetails, freightInsurance: e.target.value });
                        }}
                        disabled={!isShipmentDetailsWriteAllowed}
                      />
                    </Grid>
                  </Grid>

                  {/* Price Breakup Sub-section */}
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 2,
                      borderRadius: '10px',
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                      Price Breakup
                    </Typography>
                    <Divider sx={{ mb: 2, borderColor: alpha(theme.palette.divider, 0.08) }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography
                          sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}
                        >
                          Shipment Value (Goods)
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontSize: '14px', color: 'text.primary', mt: 0.5 }}
                        >
                          {shipmentType.currency || 'INR'} {resolvedShipmentValue.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography
                          sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}
                        >
                          Freight Amount
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '14px',
                            color: numericFreight > 0 ? 'text.primary' : 'text.secondary',
                            mt: 0.5,
                          }}
                        >
                          {shipmentType.currency || 'INR'} {numericFreight.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography
                          sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}
                        >
                          Insurance Amount
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '14px',
                            color: numericInsurance > 0 ? 'text.primary' : 'text.secondary',
                            mt: 0.5,
                          }}
                        >
                          {shipmentType.currency || 'INR'} {numericInsurance.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'primary.main', display: 'block' }}>
                          Total Declared Value
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                          {shipmentType.currency || 'INR'} {totalCombinedValue.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* DDP/DDU Section ("Who pays duty & taxes?") */}
          {isExport && !isDocument && (
            <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ReceiptLongOutlinedIcon />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'text.primary' }}>
                      Who pays duty & taxes? (DDP / DDU)
                    </Typography>
                    <Chip
                      label="Required"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary', mt: 0.25 }}>
                    Specify how import customs duties and destination taxes should be billed
                  </Typography>
                </Box>
              </Box>

              {/* Computed Values Summary */}
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      bgcolor: alpha(theme.palette.background.default, 0.6),
                      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                      Customs Value (Shipment + Freight + Insurance)
                    </Typography>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary' }}>
                      {shipmentType.currency || 'INR'} {totalCombinedValue.toFixed(2)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      bgcolor: alpha(theme.palette.background.default, 0.6),
                      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                      Duty & Taxes Value
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: hasDutyTaxValue ? '18px' : '14px',
                        fontWeight: hasDutyTaxValue ? 700 : 500,
                        color: hasDutyTaxValue ? 'primary.main' : 'text.secondary',
                        fontStyle: hasDutyTaxValue ? 'normal' : 'italic',
                      }}
                    >
                      {dutyTaxFormatted}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Segmented DDP / DDU Selector */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1 }}>
                  Choose Billing Term *
                </Typography>
                {(() => {
                  const currentDutyType = getDutyChargeType(shipmentType.dutyCharge);
                  return (
                    <Box>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          p: 0.5,
                          bgcolor: alpha(theme.palette.divider, 0.08),
                          borderRadius: '10px',
                          gap: 1,
                        }}
                      >
                        <SbButton
                          variant={currentDutyType === 'DDU' ? 'contained' : 'text'}
                          disabled={!isShipmentDetailsWriteAllowed}
                          onClick={() => handleDutyChargeChange('DDU')}
                          sx={{
                            px: 3,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            bgcolor: currentDutyType === 'DDU' ? 'primary.main' : 'transparent',
                            color: currentDutyType === 'DDU' ? 'common.white' : 'text.primary',
                            boxShadow:
                              currentDutyType === 'DDU'
                                ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                : 'none',
                            '&:hover': {
                              bgcolor:
                                currentDutyType === 'DDU' ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                            },
                          }}
                        >
                          DDU (Delivered Duty Unpaid)
                        </SbButton>
                        <SbButton
                          variant={currentDutyType === 'DDP' ? 'contained' : 'text'}
                          disabled={!isShipmentDetailsWriteAllowed}
                          onClick={() => handleDutyChargeChange('DDP')}
                          sx={{
                            px: 3,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            bgcolor: currentDutyType === 'DDP' ? 'primary.main' : 'transparent',
                            color: currentDutyType === 'DDP' ? 'common.white' : 'text.primary',
                            boxShadow:
                              currentDutyType === 'DDP'
                                ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                : 'none',
                            '&:hover': {
                              bgcolor:
                                currentDutyType === 'DDP' ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                            },
                          }}
                        >
                          DDP (Delivered Duty Paid)
                        </SbButton>
                      </Box>

                      {/* Helper text under selector */}
                      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={currentDutyType === 'DDP' ? 'Added to your charges' : 'Consignee pays on delivery'}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            bgcolor:
                              currentDutyType === 'DDP'
                                ? alpha(theme.palette.primary.main, 0.12)
                                : alpha(theme.palette.success.main, 0.12),
                            color: currentDutyType === 'DDP' ? 'primary.main' : 'success.dark',
                          }}
                        />
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                          {currentDutyType === 'DDP'
                            ? 'Import duty & taxes are added to your invoice and prepaid. Consignee pays nothing upon delivery.'
                            : 'Import duty & taxes will be collected directly from the consignee upon delivery.'}
                        </Typography>
                      </Box>

                      {isDestinationEU && (
                        <Box
                          sx={{
                            mt: 1.5,
                            p: 1.5,
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontSize: '13px', color: 'info.dark' }}>
                            For EU destinations, DDP enables prepaid duty and seamless customs clearance without
                            consignee intervention.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}
          {name !== 'Cargo Solutions Network' && (
            <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`, width: '100%' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', mb: 1.5 }}>
                Value-Added Services
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: { xs: 2, sm: 3, md: 4 },
                  width: '100%',
                }}
              >
                <Insurance
                  value={packageData.insurance}
                  onChange={value => setPackageData({ ...packageData, insurance: value })}
                />
                <Packaging
                  value={packageData.packaging}
                  onChange={value => setPackageData({ ...packageData, packaging: value })}
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <ShipmentDetailsBoxWise
        removeShipmentInfo
        showViewMoreAction
        isShipmentDetailsWriteAllowed={isShipmentDetailsWriteAllowed}
      />
      {shipmentActionControl.canUpdateShipmentDetails && (
        <Actions
          disabled={
            !isShipmentDetailsWriteAllowed || (checkForIsInTransit && !isInTransitEditAllowed) || isSaveDisabled
          }
          onSave={onSave}
        />
      )}
    </Box>
  );
}

// const IconStyles = { color: 'var(--mui-palette-primary-main)', height: '24px', width: '24px' };
interface InsuranceProps {
  value: boolean;
  onChange: (value: boolean) => void;
}
function Insurance(props: InsuranceProps) {
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isShipmentDetailsWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_details,
    PERFORM_ACTION.write,
  );

  const dispatch = useDispatch();

  const shipmentSetting = useSelector((state: RootStateType) => state.orgSetup.shipmentSetting);
  const disable = useMemo(() => !shipmentSetting?.insuranceCharge?.charge, [shipmentSetting?.insuranceCharge?.charge]);
  return (
    <Box
      className={`${disable ? 'disabled-form' : 'cursor-pointer'}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        cursor: disable ? 'not-allowed' : 'pointer',
        opacity: disable ? 0.6 : 1,
      }}
    >
      <Checkbox
        name="insurance"
        id="insurance"
        sx={{ mr: 0.5, p: 0.5 }}
        checked={!!props.value}
        onChange={() => {
          props.onChange(!props.value);
          dispatch(setInsureShipment(!props.value));
        }}
        inputProps={{ 'aria-label': 'controlled' }}
        disabled={disable || !isShipmentDetailsWriteAllowed}
      />
      <img src="/images/Boxes/Insurance.svg" alt="logistics" style={{ width: 22, height: 22, marginRight: 6 }} />
      <Typography
        component="label"
        htmlFor="insurance"
        sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', cursor: 'pointer', userSelect: 'none' }}
      >
        Insure Your Shipment
      </Typography>
    </Box>
  );
}

interface PackagingProps {
  value: boolean;
  onChange: (value: boolean) => void;
}

function Packaging(props: PackagingProps) {
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isShipmentDetailsWriteAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_details,
    PERFORM_ACTION.write,
  );

  const shipmentSetting = useSelector((state: RootStateType) => state.orgSetup.shipmentSetting);
  const disable = useMemo(() => !shipmentSetting?.packagingCharge?.charge, [shipmentSetting?.packagingCharge?.charge]);
  return (
    <Box
      className={`${disable ? 'disabled-form' : 'cursor-pointer'}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        cursor: disable ? 'not-allowed' : 'pointer',
        opacity: disable ? 0.6 : 1,
      }}
    >
      <Checkbox
        name="packaging"
        id="packaging"
        sx={{ mr: 0.5, p: 0.5 }}
        checked={!!props.value}
        onChange={() => props.onChange(!props.value)}
        inputProps={{ 'aria-label': 'controlled' }}
        disabled={disable || !isShipmentDetailsWriteAllowed}
      />
      <img src="/images/Boxes/Box.svg" alt="logistics" style={{ width: 22, height: 22, marginRight: 6 }} />
      <Typography
        component="label"
        htmlFor="packaging"
        sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', cursor: 'pointer', userSelect: 'none' }}
      >
        Add Packaging
      </Typography>
    </Box>
  );
}

function ContextWrapper() {
  return (
    <CreateShipmentWrapper>
      <ShipmentDetails />
    </CreateShipmentWrapper>
  );
}

export default ContextWrapper;
